import {
  FunctionComponent,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import "./CgPatientList.css";
import API from "../../services/rest/api";
import useApi from "../../hooks/useApi";
import useDebouncedSearch from "../../hooks/useDebouncedSearch";
import { TABLE } from "../../constants/common";
import Table from "../../atoms/Table/Table";
import Input from "../../atoms/Input/Input";
import Button from "../../atoms/Button/Button";
import moment from "moment";

interface CgPatientListProps {
  isVerified: boolean;
}

const CgPatientList: FunctionComponent<CgPatientListProps> = ({ isVerified }) => {
  const [patients, setPatients] = useState<any[]>([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(TABLE.DEFAULT_PAGINATION);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const navigate = useNavigate();

  // Debounced search - only triggers API call after 3+ characters with 500ms delay
  const debouncedSearchQuery = useDebouncedSearch(searchQuery, {
    minLength: 3,
    delay: 500,
  });

  const {
    mutate: getPatients,
    isLoading,
    response: patientsResponse,
    error: patientsError,
  } = useApi(API.GetPatients);

  // Handle API response
  useEffect(() => {
    if (Number(patientsResponse?.code || 0) === 1) {
      setPatients(patientsResponse.data || []);
      setTotalPatients(patientsResponse.total_data || 0);
      setErrorMessage("");
    } else if (patientsResponse || Number(patientsResponse?.code || 0) === 2) {
      setPatients([]);
      setTotalPatients(0);
      setErrorMessage(patientsResponse?.message || "Something went wrong!");
    } else if (patientsError || Number(patientsError?.code || 0) === 2) {
      setPatients([]);
      setTotalPatients(0);
      setErrorMessage(patientsError?.message || "Something went wrong!");
    }
  }, [patientsResponse, patientsError]);

  const fetchPatients = () => {
    const payload = {
      page: currentPage.toString(),
      per_page: pageSize.toString(),
      search: debouncedSearchQuery, // Use debounced search query
      is_verified: isVerified ? "1" : "0",
    };
    getPatients(payload);
  };

  // Fetch patients when page, pageSize, debouncedSearchQuery, or isVerified changes
  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchQuery, isVerified]);

  // Reset to first page when debounced search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const showPatientSummary = (records: any) => {
    navigate(`/patient-summary/:token`, {
      state: {
        name: records.name,
        contact: records.contact_no,
        patientId: records.patient_id,
        token: localStorage.getItem("token"),
      },
    });
  };

  // Columns for verified patients
  const verifiedPatientsColumns = [
    {
      label: "Sl. No.",
      accessor: "index",
      render: (_value: any, _row: any, rowIndex: number) =>
        (currentPage - 1) * pageSize + rowIndex + 1,
    },
    {
      label: "Name",
      accessor: "name",
      render: (value: any, row: any) => <div>{row.name || "-"}</div>,
    },
    {
      label: "Phone No",
      accessor: "contact_no",
      render: (value: any, row: any) => <div>{row.contact_no || "-"}</div>,
    },
    {
      label: "Type",
      accessor: "user_type",
      render: (value: any, row: any) => <div>{row.user_type || "-"}</div>,
    },
    {
      label: "Doctor Name",
      accessor: "doctor_name",
      render: (value: any, row: any) => <div>{row.doctor_name || "-"}</div>,
    },
    {
      label: "Cycle No",
      accessor: "visits",
      render: (value: any, row: any) => <div>{row.visits || "-"}</div>,
    },
    {
      label: "Action",
      accessor: "action",
      render: (value: any, row: any) => (
        <Button
          variant="outlined"
          onClick={() => {
            showPatientSummary(row);
          }}
        >
          View Details
        </Button>
      ),
    },
  ];

  // Columns for all patients (non-verified)
  const allPatientsColumns = [
    {
      label: "Sl. No.",
      accessor: "index",
      render: (_value: any, _row: any, rowIndex: number) =>
        (currentPage - 1) * pageSize + rowIndex + 1,
    },
    {
      label: "Name",
      accessor: "name",
      render: (value: any, row: any) => <div>{row.name || "-"}</div>,
    },
    {
      label: "Phone No",
      accessor: "contact_no",
      render: (value: any, row: any) => <div>{row.contact_no || "-"}</div>,
    },
    {
      label: "Type",
      accessor: "user_type",
      render: (value: any, row: any) => <div>{row.user_type || "-"}</div>,
    },
    {
      label: "Doctor Name",
      accessor: "doctor_name",
      render: (value: any, row: any) => <div>{row.doctor_name || "-"}</div>,
    },
    {
      label: "PSP Name",
      accessor: "PSP_Name",
      render: (value: any, row: any) => <div>{row.PSP_Name || "-"}</div>,
    },
    {
      label: "Log In Time",
      accessor: "last_active",
      render: (value: any, row: any) => (
        <div>
          {row.last_active
            ? moment(row.last_active).format("DD-MM-YYYY hh:mm A")
            : "-"}
        </div>
      ),
    },
    {
      label: "Action",
      accessor: "action",
      render: (value: any, row: any) => (
        <Button
          variant="outlined"
          onClick={() => {
            navigate(`/verify-documents/:token`, {
              state: {
                patientId: row.patient_id,
                token: localStorage.getItem("token"),
              },
            });
          }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div className="verified-patients-table-container">
      <div className="verified-patients-table-header">
        <h3 className="header-title">
          {isVerified ? "Verified Patients" : "Your Patients"}
        </h3>
      </div>
      <div className="page-actions">
        <Input
          name="search"
          id="search"
          type="text"
          placeholder="Search by name or phone no."
          value={searchQuery}
          onChange={(e: any) => setSearchQuery(e.target.value)}
          wrapperClassName="w-full"
          disabled={isLoading}
        />
      </div>
      <Table
        data={patients}
        columns={isVerified ? verifiedPatientsColumns : allPatientsColumns}
        showPagination={true}
        totalRecords={totalPatients}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        isLoading={isLoading}
        errorMessage={errorMessage}
      />
    </div>
  );
};

export default CgPatientList;
